import React, { useEffect, useRef, useState } from "react";
import styled, { css, useTheme } from "styled-components/native";
import usePostOnboarding from "../../../hooks/usePostOnboardingData";
import { useNavigationMenuSlice } from "@redux/slices/navigationMenuSlice";
import { HeaderConfig, HeaderItem } from "@components/common/Header/utils";
import HeaderSection from "@components/common/Header/HeaderSection";
import InnerHTML from "dangerously-set-html-content";
import {
  useLinkTo,
  useNavigation,
  useNavigationState,
} from "@react-navigation/native";
import { useScrollContext } from "@navigations/utils/ScrollContext";
// @ts-ignore
import { v4 as uuidv4 } from "uuid";
import { Platform } from "react-native";
import { throttle } from "lodash";
import { getActiveRouteName } from "@utils/analytics.web";
import { ActiveButtonsEnum } from "./types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useDispatch, useSelector } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { useTranslatedHTML } from "../../../hooks/useTranslatedHTML";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { DefaultTheme } from "styled-components/native";
import { BuilderAlertManager } from "@scenes/jewelry-builder/components/BuilderAlertManager";
import { resetFiltersPayload } from "@redux/slices/searchFilters";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";

const BASE_HEADER_HEIGHT = 114;
const MAX_SEARCH_WIDTH = 435;

const WebHeader = () => {
  const theme = useTheme();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { clearRouteHistory } = useNavigationHistory();

  const [allowPostOnboadingNavigation, setAllowPostOnboadingNavigation] =
    useState(false);
  const onNavigationSucceeded = (isSucceeded: boolean) => {
    if (isSucceeded) setAllowPostOnboadingNavigation(false);
  };
  usePostOnboarding(allowPostOnboadingNavigation, onNavigationSucceeded);

  const [activeButton, setActiveButton] = useState<ActiveButtonsEnum | null>(
    null
  );
  const linkTo = useLinkTo();
  const navigation = useNavigation();
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const containerRef = useRef(null);
  const newRouteName = useNavigationState((state) => getActiveRouteName(state));
  const currentRouteRef = useRef(newRouteName);
  const { getHeaderData } = useNavigationMenuSlice();
  const headerData: HeaderConfig = getHeaderData() || {
    backgroundColor: "header-bg-static",
    height: BASE_HEADER_HEIGHT,
    maxWidth: null,
    hasDivider: true,
    left: { items: [], width: "auto", containerWidth: "auto" },
    center: { items: [], width: "auto", containerWidth: "auto" },
    right: { items: [], width: "auto", containerWidth: "auto" },
  };

  const [_, __, ___, ____, _____, ______, _______, binaryScrollValue] =
    useScrollContext();
  const HEADER_HEIGHT = headerData?.height ?? BASE_HEADER_HEIGHT;

  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const handleScroll = () => {
      const newHeight = binaryScrollValue.value === 0 ? HEADER_HEIGHT : 0;
      setHeaderHeight(newHeight);
    };
    const listenerId = uuidv4();
    binaryScrollValue.addListener(listenerId, throttle(handleScroll, 300));
    return () => {
      binaryScrollValue.removeListener(listenerId);
    };
  }, [binaryScrollValue, HEADER_HEIGHT]);

  useEffect(() => {
    if (currentRouteRef.current !== newRouteName) {
      currentRouteRef.current = newRouteName;
      setHeaderHeight(HEADER_HEIGHT);
    }
  }, [newRouteName]);

  useEffect(() => {
    if (containerRef.current) {
      const links = (containerRef.current as HTMLElement)?.querySelectorAll(
        "a"
      );
      const eventListeners: Array<{
        link: Element;
        handler: (e: any) => void;
      }> = [];

      const setActiveLink = () => {
        links.forEach((link: any) => {
          if (link.getAttribute("href") === location.pathname) {
            link.classList.add("active");
          } else {
            link.classList.remove("active");
          }
        });
      };

      const removeActive = () => {
        links.forEach((link: any) => {
          link.classList.remove("active");
        });
      };

      links.forEach((link: any) => {
        const clickHandler = (e: any) => {
          e.preventDefault();

          const href = link.getAttribute("href");
          const isInternal = link.getAttribute("internal");
          const isRestricted = href.includes("isRestricted=true");

          if (userInfo.isGuest && isRestricted) {
            dispatch(setLoginModalType("default"));
            return;
          }

          const navigateFromLink = () => {
            if (href.startsWith("http") || href.startsWith("//")) {
              if (isInternal) {
                window.location.href = href;
              } else {
                window.open(href, "_blank");
              }
            } else {
              removeActive();
              link.classList.add("active");
              const linkUrl = new URL(href, window.location.origin);
              if (
                link.getAttribute("href") !==
                  location.pathname + location.search &&
                href !== "#"
              ) {
                linkTo(href);
              } else if (
                link.getAttribute("href") ===
                  location.pathname + location.search &&
                linkUrl.searchParams?.get("saved_search")
              ) {
                window.dispatchEvent(
                  new CustomEvent("deepLinkNavigation", {
                    detail: {
                      href,
                    },
                  })
                );
              }
            }
          };

          if (enableJewelryBuilder && history.length > 2) {
            BuilderAlertManager.open(navigateFromLink);
          } else {
            if (enableJewelryBuilder) {
              dispatch(resetFiltersPayload());
              if (history.length > 0) {
                // @ts-ignore
                navigation.pop(history.length);
              }
              clearRouteHistory();
            }
            navigateFromLink();
          }
        };

        link.addEventListener("click", clickHandler);
        eventListeners.push({ link, handler: clickHandler });
      });

      setActiveLink();

      return () => {
        eventListeners.forEach(({ link, handler }) => {
          link.removeEventListener("click", handler);
        });
        setActiveLink();
      };
    }
  }, [
    linkTo,
    headerData?.htmlHeader,
    enableJewelryBuilder,
    history,
    userInfo.isGuest,
    dispatch,
    navigation,
    clearRouteHistory,
  ]);

  const onPress = (buttonPressed: ActiveButtonsEnum | null) => {
    setActiveButton(buttonPressed as ActiveButtonsEnum | null);
  };

  if (!theme.isLargeScreen) return null;

  const isSearchActive = activeButton === ActiveButtonsEnum.SEARCH_MARKETPLACE;

  const translatedHTML = useTranslatedHTML(headerData?.htmlHeader);

  return (
    <Container backgroundColor={headerData?.backgroundColor}>
      <HeaderContainer
        hasDivider={headerData?.hasDivider}
        headerHeight={headerHeight}
        maxWidth={headerData?.maxWidth}
      >
        <LeftSection
          maxWidth={headerData.left?.width}
          containerWidth={headerData.left?.containerWidth}
          isSearchActive={
            isSearchActive &&
            headerData.left?.items?.some(
              (item) => item.name === HeaderItem.SEARCH
            )
          }
        >
          <HeaderSection
            sectionData={headerData.left ?? { items: [] }}
            sectionName="left"
            activeButton={activeButton as string}
            onPress={onPress}
            headerHeight={HEADER_HEIGHT}
            headerBackgroundColor={headerData?.backgroundColor}
          />
        </LeftSection>

        <CenterSection
          maxWidth={headerData.center?.width}
          containerWidth={headerData.center?.containerWidth}
          isSearchActive={
            isSearchActive &&
            headerData.center?.items?.some(
              (item) => item.name === HeaderItem.SEARCH
            )
          }
        >
          <HeaderSection
            sectionData={headerData.center ?? { items: [] }}
            sectionName="center"
            activeButton={activeButton as string}
            onPress={onPress}
            headerHeight={HEADER_HEIGHT}
            headerBackgroundColor={headerData?.backgroundColor}
          />
        </CenterSection>

        <RightSection
          maxWidth={headerData.right?.width}
          containerWidth={headerData.right?.containerWidth}
          isSearchActive={
            isSearchActive &&
            headerData.right?.items?.some(
              (item) => item.name === HeaderItem.SEARCH
            )
          }
        >
          <HeaderSection
            sectionData={headerData.right ?? { items: [] }}
            sectionName="right"
            activeButton={activeButton as string}
            onPress={onPress}
            headerHeight={HEADER_HEIGHT}
            headerBackgroundColor={headerData?.backgroundColor}
          />
        </RightSection>
      </HeaderContainer>

      {translatedHTML && (
        <div ref={containerRef} key={translatedHTML}>
          <InnerHTML html={translatedHTML} />
        </div>
      )}
    </Container>
  );
};
export default WebHeader;

const Container = styled.View<{
  backgroundColor?: string;
}>`
  background-color: ${({
    backgroundColor,
    theme,
  }: {
    backgroundColor: string;
    theme: DefaultTheme;
  }) => backgroundColor || theme["header-bg-static"]};
  z-index: 999;
`;

const HeaderContainer = styled.View<{
  hasDivider?: boolean;
  headerHeight: number;
  maxWidth?: number;
}>`
  width: 100%;
  height: ${({ headerHeight }: { headerHeight: number }) =>
    `${headerHeight}px`};
  display: flex;
  flex-direction: row;
  border-bottom-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-04"]};
  border-bottom-width: ${({ hasDivider }: { hasDivider: boolean }) =>
    hasDivider ? 1 : 0}px;
  align-items: center;
  overflow: hidden;
  transition: height 0.3s ease-out;
  ${({ maxWidth }: { maxWidth: number }) =>
    maxWidth &&
    css`
      max-width: ${maxWidth};
      width: 100%;
    `}
  margin: 0 auto;
`;

const LeftSection = styled.View<{
  maxWidth?: string;
  isSearchActive?: boolean;
  containerWidth?: any;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ containerWidth }: { containerWidth: string }) =>
    containerWidth ? "center" : "flex-start"};
  gap: 12px;

  width: ${({
    isSearchActive,
    containerWidth,
    maxWidth,
  }: {
    isSearchActive: boolean;
    containerWidth: string;
    maxWidth: string;
  }) =>
    isSearchActive
      ? "100%"
      : containerWidth
      ? `${containerWidth}px`
      : maxWidth || "auto"};

  max-width: ${({ isSearchActive }: { isSearchActive: boolean }) =>
    isSearchActive ? `${MAX_SEARCH_WIDTH}px` : "none"};
`;

const CenterSection = styled.View<{
  maxWidth?: string;
  isSearchActive?: boolean;
  containerWidth?: any;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 0 12px;

  ${({ containerWidth }: { containerWidth: string }) =>
    !containerWidth &&
    css`
      flex: 1;
    `}

  ${({
    maxWidth,
    isSearchActive,
    containerWidth,
  }: {
    maxWidth: string;
    isSearchActive: boolean;
    containerWidth: string;
  }) => {
    if (isSearchActive) {
      return css`
        max-width: ${MAX_SEARCH_WIDTH}px;
        width: 100%;
      `;
    } else if (containerWidth) {
      return css`
        width: ${containerWidth};
      `;
    }
    return maxWidth ? `width: ${maxWidth};` : "";
  }}
`;

const RightSection = styled.View<{
  maxWidth?: string;
  isSearchActive?: boolean;
  containerWidth?: any;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding-right: 12px;
  ${({
    maxWidth,
    isSearchActive,
    containerWidth,
  }: {
    maxWidth: string;
    isSearchActive: boolean;
    containerWidth: string;
  }) => {
    if (isSearchActive) {
      return css`
        max-width: ${MAX_SEARCH_WIDTH}px;
        width: 100%;
      `;
    } else if (containerWidth) {
      return css`
        justify-content: center;
        width: ${containerWidth}px;
      `;
    }
    return (
      maxWidth &&
      css`
        justify-content: flex-end;
      `
    );
  }}
`;
